import type { Meilisearch_SousUnivers } from '@/lib/api/meilisearch-types';
import { extractIdFromSlug } from '@/lib/api/utils/extractIdFromSlug';
import { SUB_UNIVERSE_ATTRIBUTE } from '@/lib/search/searchAttributes';
import type { PossibleLocales } from '@/utils/AppConfig';

/** Opérateur entre sous-univers (même valeurs que `RefinementOperator`). */
export type UniverseOperator = 'and' | 'or';

/** Opérateur du filtre Univers quand l'URL n'en précise pas. */
export const UNIVERSE_DEFAULT_OPERATOR: UniverseOperator = 'or';

/**
 * Un univers et la liste COMPLÈTE de ses sous-univers. Sert à reconnaître un
 * univers sélectionné en entier, pour l'afficher sous son seul nom (filtres
 * actifs, résumés, URL) au lieu de la liste de ses sous-univers.
 */
export interface UniverseNode {
  id: string;
  names: Record<PossibleLocales, string>;
  subUniverseIds: string[];
}

interface UniverseSplit {
  /** Univers dont tous les sous-univers sont sélectionnés. */
  universes: UniverseNode[];
  /** Sous-univers sélectionnés hors de ces univers complets. */
  subUniverseIds: string[];
}

/** Sous-univers d'un même univers, tels que les livre l'index Meili. */
export interface SubUniverseGroup {
  universeId: string;
  univers: NonNullable<Meilisearch_SousUnivers['univers']>;
  subUniverses: Meilisearch_SousUnivers[];
}

/**
 * Seul regroupement des sous-univers par univers, partagé par l'arbre
 * (buildUniverseTree) et le menu du filtre Univers (useGroupedUniverses).
 * L'index Meili `sous_univers` ne porte pas l'id de l'univers parent : on le
 * lit à la fin de son slug (`beaux-arts-10`), la même convention que la route
 * /oeuvres/[slug]. Les sous-univers sans univers ou sans id sont écartés ;
 * l'ordre est celui de la première apparition de chaque univers.
 */
export function groupSubUniversesByUniverse(
  subUniverses: readonly Meilisearch_SousUnivers[],
): SubUniverseGroup[] {
  const byId = new Map<string, SubUniverseGroup>();

  subUniverses.forEach((subUniverse) => {
    const { univers } = subUniverse;
    const universeId = extractIdFromSlug(univers?.fr?.slug ?? null);
    if (!univers || universeId === null || subUniverse.id == null) return;

    const id = String(universeId);
    const group = byId.get(id) ?? { universeId: id, univers, subUniverses: [] };
    group.subUniverses.push(subUniverse);
    byId.set(id, group);
  });

  return [...byId.values()];
}

/** Arbre des univers : leurs noms et les ids de leurs sous-univers. */
export function buildUniverseTree(
  subUniverses: readonly Meilisearch_SousUnivers[],
): UniverseNode[] {
  return groupSubUniversesByUniverse(subUniverses).map(
    ({ universeId, univers, subUniverses: subs }) => ({
      id: universeId,
      names: { fr: univers.fr.nom, en: univers.en?.nom ?? univers.fr.nom },
      subUniverseIds: subs.map((sub) => String(sub.id)),
    }),
  );
}

/**
 * Seule définition de « univers sélectionné en entier » : tous ses sous-univers
 * sont cochés (un univers sans sous-univers ne l'est jamais).
 */
export function isUniverseFullySelected(
  subUniverseIds: readonly string[],
  selected: ReadonlySet<string>,
): boolean {
  return (
    subUniverseIds.length > 0 && subUniverseIds.every((id) => selected.has(id))
  );
}

/** Opérateur du filtre Univers dans un état d'index, défaut compris. */
export function getUniverseOperator(
  mslOperators: Readonly<Record<string, string>> | undefined,
): UniverseOperator {
  return mslOperators?.[SUB_UNIVERSE_ATTRIBUTE] === 'and'
    ? 'and'
    : UNIVERSE_DEFAULT_OPERATOR;
}

/**
 * Sépare une sélection de sous-univers en univers complets et en sous-univers
 * isolés, dans l'ordre de la sélection.
 */
function splitCompleteUniverses(
  selectedIds: readonly string[],
  tree: readonly UniverseNode[],
): UniverseSplit {
  const selected = new Set(selectedIds);
  const completeBySubId = new Map<string, UniverseNode>();
  tree.forEach((universe) => {
    if (!isUniverseFullySelected(universe.subUniverseIds, selected)) return;
    universe.subUniverseIds.forEach((id) => completeBySubId.set(id, universe));
  });

  const universes: UniverseNode[] = [];
  const subUniverseIds: string[] = [];
  selectedIds.forEach((id) => {
    const universe = completeBySubId.get(id);
    if (!universe) {
      subUniverseIds.push(id);
    } else if (!universes.includes(universe)) {
      universes.push(universe);
    }
  });

  return { universes, subUniverseIds };
}

/**
 * Regroupement à afficher : en OU, un univers complet vaut l'univers ; en ET,
 * tous ses sous-univers ne le valent pas, on garde donc les sous-univers.
 * Point d'entrée commun de l'URL, des filtres actifs et des résumés.
 */
export function groupUniverseSelection(
  selectedIds: readonly string[],
  operator: UniverseOperator,
  tree: readonly UniverseNode[],
): UniverseSplit {
  if (operator === 'and') {
    return { universes: [], subUniverseIds: [...selectedIds] };
  }
  return splitCompleteUniverses(selectedIds, tree);
}

/** Liste les sous-univers des univers donnés ; un univers inconnu est ignoré. */
export function expandUniverseIds(
  universeIds: readonly string[],
  tree: readonly UniverseNode[],
): string[] {
  return universeIds.flatMap(
    (id) => tree.find((universe) => universe.id === id)?.subUniverseIds ?? [],
  );
}
